import { useEffect, useState } from 'react';

/** Значения breakpoint-ов Tailwind v4 (min-width) */
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

/** Объект, где ключ — имя breakpoint-а, значение — true если ширина окна >= этого breakpoint-а */
export type Breakpoints = Record<BreakpointKey, boolean>;

const getSnapshot = (): Breakpoints => ({
  sm: window.innerWidth >= BREAKPOINTS.sm,
  md: window.innerWidth >= BREAKPOINTS.md,
  lg: window.innerWidth >= BREAKPOINTS.lg,
  xl: window.innerWidth >= BREAKPOINTS.xl,
  '2xl': window.innerWidth >= BREAKPOINTS['2xl'],
});

/**
 * Возвращает объект активных breakpoint-ов.
 * Каждый ключ — имя breakpoint-а Tailwind, значение — true если viewport >= этого breakpoint-а.
 *
 * @example
 * const bp = useBreakpoints();
 * if (bp.lg) { // >= 1024px }
 */
export function useBreakpoints(): Breakpoints {
  const [breakpoints, setBreakpoints] = useState<Breakpoints>(getSnapshot);

  useEffect(() => {
    const cleanups: (() => void)[] = [];

    for (const width of Object.values(BREAKPOINTS)) {
      const mql = window.matchMedia(`(min-width: ${width}px)`);
      const onChange = () => setBreakpoints(getSnapshot());
      mql.addEventListener('change', onChange);
      cleanups.push(() => mql.removeEventListener('change', onChange));
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return breakpoints;
}
